import { useAppContext } from "@context/app";
import Button from "@elements/button";
import GoogleMap from "@elements/googleMap";
import Marker from "@elements/marker";
import { IAreaDetailInput } from "@types";
import { commonApi } from "gate";
import googleMapReact from "google-map-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { HiOutlineLocationMarker } from "react-icons/hi";
import MapAutocomplete from "./mapAutocopmlete";

interface MapViewProps {
  onSubmit: (data: {
    street: string;
    lat: number;
    lng: number;
    newFormValues: { [key: string]: any };
  }) => void;
  area: IAreaDetailInput;
  defaultLocation: { lat: number; lng: number };
  boundaryPoints: { lat: number; lng: number }[];
  goToCurrentLocation?: boolean;
}

const MapView: React.FC<MapViewProps> = ({
  boundaryPoints,
  onSubmit,
  area,
  defaultLocation,
  goToCurrentLocation,
}) => {
  const t = useTranslations();
  const { country } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const [markerPosition, setMarkerPosition] = useState(
    area.center || {
      lat: 29.372182,
      lng: 47.982538,
    }
  );
  const [center, setCenter] = useState(area.center || { lat: 29.372182, lng: 47.982538 });
  const [draggable, setDraggable] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [mapValue, setMapValue] = useState<{
    street: string;
    lat: number;
    lng: number;
    newFormValues: { [key: string]: any };
  }>({
    street: "",
    lat: 29.372182,
    lng: 47.982538,
    newFormValues: {},
  });

  // useEffect(() => {
  //   if (country) {
  //     // setCenter({ ...defaultLocation });
  //     setMarkerPosition({ ...defaultLocation });
  //     getGeolocation({
  //       lat: defaultLocation.lat,
  //       lng: defaultLocation.lng,
  //       country: country.code2,
  //     });
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [defaultLocation, country]);

  useEffect(() => {
    if (country && area) {
      if (defaultLocation && !goToCurrentLocation) {
        setCenter({ ...defaultLocation });
        setMarkerPosition({ ...defaultLocation });
        getGeolocation({
          lat: defaultLocation.lat,
          lng: defaultLocation.lng,
          country: country.code2,
        });
      } else {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async function (position) {
              const lat = position.coords.latitude;
              const lng = position.coords.longitude;
              setCenter({ lat, lng });
              setMarkerPosition({ lat, lng });
              getGeolocation({ lat, lng, country: country.code2 });
            },
            function (error) {
              setMarkerPosition({ ...area.center });
              setCenter({ ...area.center });
              getGeolocation({ ...area.center, country: country.code2 });
            }
          );
        } else {
          setMarkerPosition({ ...area.center });
          setCenter({ ...area.center });
          getGeolocation({ ...area.center, country: country.code2 });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLocation, area, country]);

  if (!country) return null;

  const onCircleInteraction = (childKey: any, childProps: any, mouse: any) => {
    setDraggable(false);
    setMarkerPosition({
      lat: mouse.lat,
      lng: mouse.lng,
    });
  };
  const onCircleInteraction3 = (childKey: any, childProps: any, mouse: any) => {
    setDraggable(true);
    getGeolocation({
      lat: mouse.lat,
      lng: mouse.lng,
      country: country.code2,
    });
  };

  const handelOnClick = (e: googleMapReact.ClickEventValue) => {
    setMarkerPosition({ lat: e.lat, lng: e.lng });
    getGeolocation({ lat: e.lat, lng: e.lng, country: country.code2 });
  };

  const handelSearch = (e: { lat: number; lng: number }) => {
    setMarkerPosition(e);
    getGeolocation({ lat: e.lat, lng: e.lng, country: country.code2 });
    setCenter(e);
  };

  const handleApiLoaded = (map: any, maps: any) => {
    const triangleCoords = boundaryPoints;
    let bermudaTriangle = new maps.Polygon({
      paths: triangleCoords,
      strokeColor: "#008AF6",
      strokeOpacity: 1,
      strokeWeight: 1,
      fillColor: "#008AF6",
      fillOpacity: 0.3,
    });
    bermudaTriangle.setMap(map);
  };
  const confirmLocation = () => {
    onSubmit(mapValue);
  };

  const getGeolocation = async (data: { lat: number; lng: number; country: string }) => {
    setIsLoading(true);
    try {
      const res = await commonApi.geoLocate(data).fetch();
      // const res = {
      //   data: {
      //     address: "test",
      //     area: { id: area?.id},
      //     newFormValues: { test: "test" },
      //   },
      // };
      if (res.data?.address && res.data.area.id === area?.id) {
        setMapValue({
          street: res.data?.address,
          lat: data.lat,
          lng: data.lng,
          newFormValues: res.data?.extracted_maps_address || {},
        });
        setIsValid(true);
      } else if (area) {
        setIsValid(false);
        setMarkerPosition({ ...area.center });
        setCenter({ ...area.center });
        getGeolocation({ ...area.center, country: country.code2 });
      } else {
        setIsValid(false);
      }
    } catch (error) {
      setMapValue({ street: "", lat: data.lat, lng: data.lng, newFormValues: {} });
      setIsValid(false);
      if (area) {
        setIsValid(false);
        setMarkerPosition({ ...area.center });
        setCenter({ ...area.center });
        // getGeolocation({ ...area.center, country: country.code2 });
      }
    }
    setIsLoading(false);
  };
  return (
    <>
      <div className=" w-full relative mb-3">
        <MapAutocomplete
          area={area}
          action={(location: { lat: number; lng: number }) => {
            handelSearch(location);
          }}
          boundaryPoints={boundaryPoints}
        />
      </div>
      <div className="w-full h-[322px] bg-[#F5F5F5] rounded-xl mb-5 overflow-hidden">
        <GoogleMap
          options={{
            zoomControl: false,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
          }}
          yesIWantToUseGoogleMapApiInternals
          onClick={(e: googleMapReact.ClickEventValue) => handelOnClick(e)}
          draggable={draggable}
          onChildMouseDown={onCircleInteraction}
          onChildMouseUp={onCircleInteraction3}
          onChildMouseMove={onCircleInteraction}
          center={center}
          defaultZoom={15}
          onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
        >
          <Marker
            key={"place.id"}
            text={"place.name"}
            lat={markerPosition.lat}
            lng={markerPosition.lng}
          />
        </GoogleMap>
      </div>
      <div className="w-full">
        {isValid ? (
          <p className=" font-normal text-base text-[#333] text-center flex flex-wrap justify-center items-center">
            {" "}
            <span className="text-[#999] mr-1">
              {" "}
              <HiOutlineLocationMarker size={18} />
            </span>
            {mapValue.street}
          </p>
        ) : (
          <p className=" font-normal text-base text-[#333] text-center flex flex-wrap justify-center items-center">
            {t("client_general_wedonotservicehere")}
          </p>
        )}
      </div>
      <div className="w-full mt-5">
        <Button
          className="m-auto w-72 h-12 font-extrabold text-base rounded-full"
          size="slim"
          isDisabled={isLoading || !isValid}
          loading={isLoading}
          onClick={() => confirmLocation()}
        >
          {t("client_buttons_confirmpin")}
        </Button>
      </div>
    </>
  );
};

export default MapView;
